import AppEvents from 'lib/AppEvents';
import IDbOrCacheStore from 'lib/data/stores/IDbOrCacheStore';
import ApiService from 'services/ApiService';
import EventService from 'services/EventService';
import { getLogger } from 'utils/logging';
import { ensureRequest, handlePolledRequest } from 'utils/RequestUtils';

const DATE_FORMAT = 'YYYY-MM-DD';
const { log, warn } = getLogger('TrackingService', { color: 'blue', enabled: false });

const CACHE_ALL = false;

const _getCacheKey = (prefix, trackingId, dateRange, opts) => {
    let dateKey = '';
    if (dateRange) {
        if (dateRange.start_date) {
            dateKey = dateRange.start_date;
        }
        if (dateRange.end_date) {
            dateKey += '_to_' + dateRange.end_date;
        }
    }

    let optsKey = '';
    if (opts) {
        Object.keys(opts).forEach((k) => {
            optsKey += `&${k}=${opts[k]}`;
        });
    }

    return `TRACKING_${prefix}_${trackingId}_${dateKey}_${optsKey}`;
};

class TrackingService {
    private store: IDbOrCacheStore;

    constructor() {
        this.store = new IDbOrCacheStore('TS_');
        EventService.subscribe(AppEvents.LOGIN, async () => {
            await this.store.clearAll();
        });
        EventService.subscribe(AppEvents.LOGOUT, async () => {
            await this.store.clearAll();
        });
    }

    public clearData = async () => {
        await this.store.clear();
    }

    // Returns events related to the tracking ID, cached if available.
    getEvents = (trackingId, { timeRange, ...opts }, useCached = CACHE_ALL) => {
        return new Promise(async (resolve, reject) => {
            try {
                let cacheKey = _getCacheKey('events', trackingId, timeRange, opts);
                let data;

                if (useCached) {
                    data = await this.store.get(cacheKey);
                    if (data) return resolve(data);
                }

                log('getEvents', cacheKey);

                let r = await ensureRequest(async () => {
                    return await ApiService.post(`/tracking/events/${trackingId}`, { timeRange, ...opts });
                });

                if (r?.success) {
                    data = r.events;
                    await this.store.set(cacheKey, data);
                } else {
                    return reject(r);
                }

                return resolve(data);
            } catch (e) {
                console.log('FAILED REQUEST getEvents', e);
                return reject(e);
            }
        });
    };

    // Returns events related to the tracking ID, cached if available.
    getPurchaseEvents = (trackingId, { timeRange, ...opts }, useCached = CACHE_ALL, dataId = '') => {
        return new Promise(async (resolve, reject) => {
            try {
                let cacheKey = _getCacheKey(dataId || `purchase-events`, trackingId, timeRange, opts);
                let data;

                if (useCached) {
                    data = await this.store.get(cacheKey);
                    if (data) return resolve(data);
                }

                log('getPurchaseEvents', dataId, timeRange, cacheKey)

                let r = await handlePolledRequest(async (isJobCompleted) => {
                    try {
                        log('Fetching new getPurchaseEvents ...');
                        return await ensureRequest(async () => {
                            try {
                                return await ApiService.post(`/tracking/purchase-events/${trackingId}`, { timeRange, ...opts, dataId: cacheKey });
                            } catch (e) {
                                throw e;
                            }
                        });
                    } catch (e) {
                        throw e;
                    }
                });
                log('getPurchaseEvents response:', r)

                if (r.success && r.data) {
                    data = r.data;
                    await this.store.set(cacheKey, data);
                }

                return resolve(data);
            } catch (e) {
                console.log('FAILED REQUEST getPurchaseEvents', e);
                return reject(e);
            }
        });
    };

    // Returns events related to the tracking ID, cached if available.
    getPurchaseEvents_NonAttributed = (trackingId, { timeRange, ...opts }, useCached = CACHE_ALL, idHelper = '') => {
        return new Promise(async (resolve, reject) => {
            let cacheKey = _getCacheKey(`purchase-events_(NA)_${idHelper}`, trackingId, timeRange, opts);
            let data;

            if (useCached) {
                data = await this.store.get(cacheKey);
                if (data) return resolve(data);
            }

            //console.log('getting new purchase events...', cacheKey)
            let r = await ensureRequest(async () => await ApiService.post(`/tracking/purchase-events-non-attributed/${trackingId}`, { timeRange, ...opts }));

            if (r?.success) {
                log('getPurchaseEvents_NonAttributed response:', idHelper, cacheKey, r.data);
                data = r.data;
                await this.store.set(cacheKey, data);
            } else {
                return reject(r);
            }

            return resolve(data);
        });
    };

    // Returns events related to the tracking ID, cached if available.
    getEventsForPurchase = (purchase, useCached = CACHE_ALL) => {
        return new Promise(async (resolve, reject) => {
            try {
                if (!purchase || !purchase.tracking_id || (!purchase.orderId && !purchase.checkoutId)) {
                    return;
                }

                let pId = purchase.orderId || purchase.checkoutId;
                let allEventPromises = [];

                const getEventsBy = async (params) => {
                    let cacheKey = _getCacheKey(`journey-events_${pId}`, purchase.tracking_id, undefined, params);
                    let data = await this.store.get(cacheKey);
                    if (!data || !useCached) {
                        let r = await ensureRequest(async () => await ApiService.post(`/tracking/events`, params))
                        if (r?.success) {
                            data = { params, events: r.events };
                            await this.store.set(cacheKey, data);
                        } else {
                            throw r;
                        }
                    }
                    return data;
                };

                let timeRange = { end_date: purchase.orderDate || purchase.created }; // todo: deprectate created

                // get events by ip:
                if (purchase.ip) {
                    allEventPromises.push(getEventsBy({ ip: purchase.ip, timeRange }));
                }

                // get events by client fingerprint:
                if (purchase.client_fingerprint) {
                    allEventPromises.push(getEventsBy({ client_fingerprint: purchase.client_fingerprint, timeRange }));
                }

                if (purchase.sessionId) {
                    allEventPromises.push(getEventsBy({ session: purchase.sessionId, timeRange }));
                }

                let results = await Promise.all(allEventPromises);

                log('Events for purchase', purchase, results);

                return resolve(results);
            } catch (e) {
                return reject(e);
            }
        });
    };

    // Returns events related to the tracking ID, cached if available.
    getPurchaseJourney = (purchase, useCached = CACHE_ALL) => {
        return new Promise(async (resolve, reject) => {
            try {
                let data = {};
                if (!purchase || !purchase.tracking_id || (!purchase.ip && !purchase.client_fingerprint && !purchase.session && !purchase.parent_session)) {
                    console.log('purchase has incomplete data', purchase)
                    alert('Purchase has incomplete data');
                    reject("Purchase has incomplete data");
                    return 0;
                }

                let r = await ensureRequest(async () => await ApiService.post(`/tracking/purchase-journey`, { purchase }));
                if (r?.success) {
                    data = { events: r.events };
                    //await this.store.set(cacheKey, newData);
                } else {
                    return reject(r);
                }

                return resolve(data);
            } catch (e) {
                console.log('FAILED REQUEST getPurchaseJourney', e, purchase);
                return reject(e);
            }
        });
    };

    // Returns events related to the campaigns/adsets/ads, cached if available.
    getEventStatsByCampaign = (trackingId, { timeRange, ...opts }, useCached = true, dataId = '') => {
        return new Promise(async (resolve, reject) => {
            try {
                let cacheKey = _getCacheKey(dataId || `event-stats-by-campaign`, trackingId, timeRange, opts);
                let data;

                if (useCached) {
                    data = await this.store.get(cacheKey);
                    if (data) return resolve(data);
                }

                log('getEventStatsByCampaign', cacheKey, dataId);

                let r = await handlePolledRequest(async () => {
                    try {
                        return await ensureRequest(async () => {
                            try {
                                return await ApiService.post(`/tracking/event-stats-campaign/${trackingId}`, { timeRange, ...opts, dataId: cacheKey });
                            } catch (e) {
                                throw e;
                            }
                        })
                    } catch (e) {
                        throw e;
                    }
                });
                log('getEventStatsByCampaign response:', data);

                if (r.success && r.data) {
                    data = r.data;
                    await this.store.set(cacheKey, data);
                }

                return resolve(data);
            } catch (e) {
                console.log('FAILED REQUEST getEventStatsByCampaign', e, dataId);
                return reject(e);
            }
        });
    };

    // Returns just the event counts, cached if available.
    getEventStats = (trackingId, { timeRange, ...opts }, useCached = true, dataId = '') => {
        return new Promise(async (resolve, reject) => {
            try {
                let cacheKey = _getCacheKey(dataId || `event-stats`, trackingId, timeRange, opts);
                let data;

                if (useCached) {
                    data = await this.store.get(cacheKey);
                    if (data) return resolve(data);
                }

                log('getEventStats', cacheKey);

                let r = await ensureRequest(async () => {
                    try {
                        return await ApiService.post(`/tracking/event-stats/${trackingId}`, { timeRange, ...opts });
                    } catch (e) {
                        throw e;
                    }
                });

                if (r?.success) {
                    data = r.stats;
                    log('getEventStats response:', cacheKey, r.stats, idHelper);
                    await this.store.set(cacheKey, data);
                } else {
                    return reject(r);
                }

                return resolve(data);
            } catch (e) {
                console.log('FAILED REQUEST getEventStats', e, idHelper);
                return reject(e);
            }
        });
    };

    getAllAccounts = (trackingId, params) => {
        return new Promise((resolve, reject) => {
            ApiService.get(`/admin/users/accounts`, params)
                .then((r) => {
                    return r.success ? resolve(r.users) : reject(r);
                })
                .catch((e) => reject(e));
        });
    };

    // getAccountReport = (trackingId, params) => {
    //     return new Promise((resolve, reject) => {
    //         ApiService.post(`/tracking/report/${trackingId}`, params)
    //             .then(r => {
    //                 if (!r.success) {
    //                     return reject(r);
    //                 }
    //                 resolve(r.report);
    //             })
    //             .catch(e => reject(e))
    //     });

    //     return axios.get(`${process.env.API_URL}/users`)
    //         .then(response => { return response.data.users });
    // }

    // store = (trackingId, data) => {
    //     return new Promise((resolve, reject) => {
    //         ApiService.post(`/tracking/events`, { trackingId, ...data })
    //             .then(r => {
    //                 console.log('tracking store', r)
    //                 if (!r.success) {
    //                     return reject(r);
    //                 }
    //                 resolve(r.report);
    //             })
    //             .catch(e => reject(e))
    //     });
    // }

    // store = (data) => {
    //     return axios.post(`${process.env.API_URL}/metric-events`, data)
    //         .then(response => { return response });
    // }
}

export default new TrackingService();
